import { usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Printer, ArrowLeft } from "lucide-react"
import { assetTypeLabel } from "@/lib/assetTypes"
import { formatCNPJ } from "@/lib/masks"
import type { AssetType } from "@/lib/assetTypes"

interface ContractAccount {
  name: string
  tax_name: string | null
  tax_id: string | null
  phone: string | null
  street: string | null
  city: string | null
  state: string | null
  logo_url: string | null
}

interface ContractCustomer {
  id: string
  name: string
  tax_name: string | null
  tax_id: string | null
  email: string | null
  phone: string | null
  street: string | null
  city: string | null
  state: string | null
}

interface ContractAsset {
  id: string
  code: string
  assetable_type: AssetType
}

interface ContractPreviewPageProps {
  account: ContractAccount
  customer: ContractCustomer
  assets: ContractAsset[]
  generatedAt: string
}

function formatAddress(street?: string | null, city?: string | null, state?: string | null): string {
  return [street, city, state].filter(Boolean).join(", ")
}

export default function ContractPreview() {
  const { t, i18n } = useTranslation()
  const { account, customer, assets, generatedAt } = usePage<ContractPreviewPageProps>().props

  const date = new Date(generatedAt)
  const formattedDate = date.toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

  const lenderAddress = formatAddress(account.street, account.city, account.state)
  const borrowerAddress = formatAddress(customer.street, customer.city, customer.state)

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      {/* Toolbar - hidden when printing */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("contracts.preview.back")}
        </Button>
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="w-4 h-4 mr-2" />
          {t("contracts.preview.print")}
        </Button>
      </div>

      {/* Contract document */}
      <div className="max-w-[210mm] mx-auto bg-white shadow-lg print:shadow-none my-8 print:my-0 p-16 print:p-12 font-serif">
        {/* Header with logo */}
        {account.logo_url && (
          <div className="flex justify-center mb-8">
            <img
              src={account.logo_url}
              alt={account.name}
              className="max-h-20 object-contain"
            />
          </div>
        )}

        {/* Title */}
        <h1 className="text-xl font-bold text-center mb-10 tracking-wide">
          {t("contracts.document.title")}
        </h1>

        {/* Parties */}
        <div className="space-y-4 mb-8 text-sm leading-relaxed">
          <p>
            {t("contracts.document.lenderIntro", {
              taxName: account.tax_name || account.name,
              taxId: account.tax_id ? formatCNPJ(account.tax_id) : "—",
              address: lenderAddress || "—",
            })}
          </p>
          <p>
            {t("contracts.document.borrowerIntro", {
              name: customer.tax_name || customer.name,
              taxId: customer.tax_id ? formatCNPJ(customer.tax_id) : "—",
              address: borrowerAddress || "—",
            })}
          </p>
        </div>

        {/* Clause 1 */}
        <div className="mb-6">
          <h2 className="font-bold text-sm mb-2">{t("contracts.document.clause1Title")}</h2>
          <p className="text-sm leading-relaxed mb-4">{t("contracts.document.clause1Body")}</p>

          <div className="rounded border text-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 font-bold">{t("contracts.document.tableNumber")}</TableHead>
                  <TableHead className="font-bold">{t("contracts.document.tableCode")}</TableHead>
                  <TableHead className="font-bold">{t("contracts.document.tableType")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((asset, index) => (
                  <TableRow key={asset.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell className="font-medium">{asset.code}</TableCell>
                    <TableCell>{assetTypeLabel(asset.assetable_type, t)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Clause 2 */}
        <div className="mb-6">
          <h2 className="font-bold text-sm mb-2">{t("contracts.document.clause2Title")}</h2>
          <ol className="text-sm leading-relaxed list-none space-y-1 pl-4">
            <li>a) {t("contracts.document.clause2a")}</li>
            <li>b) {t("contracts.document.clause2b")}</li>
            <li>c) {t("contracts.document.clause2c")}</li>
            <li>d) {t("contracts.document.clause2d")}</li>
          </ol>
        </div>

        {/* Clause 3 */}
        <div className="mb-6">
          <h2 className="font-bold text-sm mb-2">{t("contracts.document.clause3Title")}</h2>
          <p className="text-sm leading-relaxed">{t("contracts.document.clause3Body")}</p>
        </div>

        {/* Clause 4 */}
        <div className="mb-10">
          <h2 className="font-bold text-sm mb-2">{t("contracts.document.clause4Title")}</h2>
          <p className="text-sm leading-relaxed">{t("contracts.document.clause4Body")}</p>
        </div>

        {/* Date */}
        <p className="text-sm text-center mb-16">
          {t("contracts.document.dateLine", {
            city: account.city || "_______________",
            date: formattedDate,
          })}
        </p>

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-16 text-center text-sm">
          <div>
            <div className="border-t border-black pt-2">
              <p className="font-bold">{t("contracts.document.lender")}</p>
              <p>{account.tax_name || account.name}</p>
            </div>
          </div>
          <div>
            <div className="border-t border-black pt-2">
              <p className="font-bold">{t("contracts.document.borrower")}</p>
              <p>{customer.tax_name || customer.name}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
